import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef
} from 'react';
import * as Sentry from '@sentry/react';
import { BenchmarkFilters, BenchmarkTypes, Lateralities } from 'constants.js';
import { useLazyQuery } from '@apollo/client';
import { QUERY_GET_TESTDATA_BY_GROUP } from 'services/aws/session-query';
import TestData from 'models/TestData';
import { StoreContext } from 'index';
import {
  sort,
  SORT_DATA_TYPES,
  SORT_TYPES,
  sortByArray,
  sortResultRowsOnCol
} from 'utils/sort';
import { ROUTE_RESULTS_ENTITY_GROUP } from 'routes/RouteList';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { groupBy } from 'utils/array';
import { getAverageOfGroup, getDataRows } from 'utils/results-benchmark';
import { format } from 'date-fns';
import { useStore } from 'stores/Store';
import { QUERY_GET_TESTS } from 'services/aws/testsets-query';
import TestItemResult from 'models/TestItemResult';

export const ResultsPanelContext = createContext({});

const getTestItemResultObj = (r, laterality, locale, testDataResult) => {
  return {
    key: `${r.id}_${laterality}`,
    id: `${r.id}_${laterality}`,
    testItemId: r.id,
    value: `${r.id}_${laterality}`,
    laterality: Number(laterality),
    label: r.title,
    hidden: r.hidden,
    testSetId: testDataResult.testSet?.id,
    testSetIds: [testDataResult.testSet?.id],
    category: r.category,
    testDate: testDataResult.testDataDate
  };
};

const ResultsPanelContextProvider = ({ children }) => {
  const {
    authStore: {
      user: { rootEntityId }
    },
    uiState: { locale }
  } = useContext(StoreContext);

  const { replace } = useHistory();
  const location = useHistory();
  const { groupId, benchmarkId } = useParams();
  const selectedHeaders = useStore(state => state.selectedHeaders);
  const { setSelectedHeaders, resetSelectedHeaders } = useStore(state => state);

  const usersArray = useRef([]);
  const testItemsArray = useRef([]);
  const testSetsArray = useRef([]);

  const [persons, setPersons] = useState([]);
  const [personsSortDirection, setPersonsSortDirection] = useState(false);
  const [testItems, setTestItems] = useState([]);
  const [groupedTestItems, setGroupedTestItems] = useState([]);
  const [testSets, setTestSets] = useState([]);
  const [type, setType] = useState(BenchmarkTypes.DEFAULT);
  const [benchmarkFilter, setBenchmarkFilter] = useState(BenchmarkFilters.LAST);
  const [rows, setRows] = useState([]);
  const [sidePanelData, setSidePanelData] = useState(null);
  const [sortIndex, setSortIndex] = useState(-1);
  const [selectedRow, setSelectedRow] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (groupId) {
      resetSelectedHeaders();
    }
  }, [groupId]);

  const [fetchBenchmarkedData, { loading, data, error: fetchError, refetch }] =
    useLazyQuery(QUERY_GET_TESTDATA_BY_GROUP);
  const [fetchTests] = useLazyQuery(QUERY_GET_TESTS);

  useEffect(() => {
    const fetchData = async () => {
      const { data: testsData } = await fetchTests({
        variables: { entityId: rootEntityId }, // , benchmarkType: TestSetV2Type.PREVENTION
        fetchPolicy: 'cache-first'
      });

      if (testsData?.getTests) {
        const tests = testsData.getTests;
        const testItemsMap = tests.flatMap(
          test =>
            test?.testItems &&
            test.testItems.map(item => ({
              ...new TestItemResult({
                ...item,
                category: test.category,
                laterality: test.laterality,
                input_type: item.inputtype
              })
            }))
        );
        setTestItems(
          testItemsMap.filter(ti => {
            if (!ti) return false;
            if (typeof ti?.hidden === 'boolean') {
              return !ti?.hidden;
            } else {
              return false; // if no hidden property, we assume it's false
            }
          })
        );
      }

      await fetchBenchmarkedData({
        variables: {
          entityId: groupId,
          benchmarkId: benchmarkId !== 'default' ? benchmarkId : null
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true
      });
    };

    if (groupId) {
      fetchData().catch(error => {
        console.log('Error fetching benchmarked data:', error);
        Sentry.captureException(error);
      });
    }
  }, [groupId, fetchBenchmarkedData, benchmarkId, location]);

  useEffect(() => {
    const fetchS3File = async url => {
      const response = await fetch(url);
      return await response.json();
    };
    const parseTestData = async testData => {
      const persons = [];
      const activeTestItems = [];
      const testSets = [];

      for (const td of testData) {
        const testDataResult = new TestData(td);
        let personResults;
        personResults =
          testDataResult.result && Array.isArray(testDataResult.result)
            ? testDataResult.result.map(r => {
                const testItem = testItems.find(ti => ti?.id === r?.id);
                if (!testItem) return null;
                return {
                  id: r.id,
                  testId: r.testId,
                  sessionDate: r.sessionDate,
                  testDate: testDataResult.testDataDate,
                  benchmarked: r.benchmarked,
                  result: r.result,
                  values: r.values,
                  unit: testItem?.unit,
                  laterality: testItem?.laterality,
                  input_type: testItem?.input_type,
                  input_type_parameters: testItem.input_type_parameters,
                  showDecimals: testItem.showDecimals,
                  isOption: testItem.input_type === 'select_with_options'
                };
              })
            : [];

        persons.push({
          ...td.person,
          label: `${td.person.firstname} ${td.person.lastname}`,
          results: [...personResults]
        });

        if (Array.isArray(testDataResult.result)) {
          testDataResult.result.forEach(r => {
            if (!r.hidden) {
              if (r?.result) {
                Object.keys(r.result).forEach(laterality => {
                  activeTestItems.push(
                    getTestItemResultObj(r, laterality, locale, testDataResult)
                  );
                });
              }
            }
            if (r?.result) {
              testSets.push({ ...td.testSet });
            }
          });
        }
      }

      const uniquePersons = persons.reduce((newArray, item) => {
        const user = newArray.find(i => i.id === item.id);
        if (user) {
          user.value = user.id;
          user.results = [...user.results, ...item.results];
          return newArray;
        } else {
          return [...newArray, item];
        }
      }, []);

      const uniqueTestItems = activeTestItems
        .reduce((newArray, item) => {
          const testItem = newArray.find(i => i.id === item.id);
          if (testItem) {
            testItem.testSetIds = Array.from(
              new Set([...testItem.testSetIds, ...item.testSetIds])
            );
            return newArray;
          } else {
            return [...newArray, item];
          }
        }, [])
        .map(item => {
          const testItem = testItems.find(ti => ti?.id === item?.testItemId);
          if (!testItem) return null;
          return {
            ...testItem,
            input_type: testItem.inputtype,
            isOption: testItem.inputtype === 'select_with_options',
            ...item,
            category: testItem?.category || 'Uncategorized',
            label: `${testItem?.copy?.[locale]?.title ?? testItem.title} ${
              Number(item.laterality) !== 2
                ? `- [${Lateralities[item.laterality]}]`
                : ''
            }`
          };
        })
        .filter(i => i);

      const uniqueTestSets = testSets.reduce((newArray, item) => {
        if (newArray.find(i => i.id === item.id)) {
          return newArray;
        } else {
          return [...newArray, item];
        }
      }, []);

      // group persons test results for easy access
      uniquePersons.forEach(person => {
        person.results = groupBy(person.results, ['id']);
      });

      testItemsArray.current = sort(uniqueTestItems, {
        keys: [{ key: 'label' }]
      });

      usersArray.current = sort(uniquePersons, {
        keys: [{ key: 'label' }]
      });

      testSetsArray.current = sort(uniqueTestSets, {
        keys: [{ key: 'title' }]
      });

      const groupedTests = groupBy(testItemsArray.current, ['category']);
      const groupedTestsArray = Object.keys(groupedTests).map(key => ({
        label: key,
        options: groupedTests[key].map(r => {
          if (r.hidden) return null;
          return {
            value: r.id,
            label: r.label
          };
        })
      }));

      updateRowData({
        persons: usersArray.current,
        testCols: selectedHeaders,
        type,
        benchmarkFilter,
        sortIndex
      });

      setGroupedTestItems(
        sort(groupedTestsArray, {
          keys: [{ key: 'label', dataType: SORT_DATA_TYPES.STRING }]
        })
      );
      setTestSets(testSetsArray.current);
    };

    if (data?.getTestDataV2OfEntity && data?.getTestDataV2OfEntity.length > 0) {
      setError(null);
      const resultObj = data?.getTestDataV2OfEntity?.[0]?.result
        ? JSON.parse(data.getTestDataV2OfEntity[0].result)
        : null;

      if (resultObj?.s3) {
        fetchS3File(resultObj.s3).then(data => {
          parseTestData(data);
        });
      } else {
        parseTestData(data.getTestDataV2OfEntity);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (fetchError) {
      if (
        fetchError.graphQLErrors.find(
          e => e.errorType === 'Lambda:ExecutionTimeoutException'
        )
      ) {
        setError('Timeout');
        refetch();
      } else {
        usersArray.current = [];
        testItemsArray.current = [];
        testSetsArray.current = [];
        setPersons([]);
        setTestItems([]);
        setTestSets([]);

        setError(fetchError);
      }
    }
  }, [fetchError]);

  const onChangeType = type => {
    setType(type);
    updateRowData({
      persons: usersArray.current,
      testCols: selectedHeaders,
      benchmarkType: type,
      benchmarkFilter,
      sortIndex
    });
  };

  const onChangeBenchmark = benchmarkId => {
    const route = generatePath(ROUTE_RESULTS_ENTITY_GROUP, {
      groupId,
      popup: 'default',
      entityId: rootEntityId,
      benchmarkId
    });
    replace(route);
  };

  const onChangeBenchmarkFilter = benchmarkFilter => {
    setBenchmarkFilter(benchmarkFilter);
    updateRowData({
      persons: usersArray.current,
      testCols: selectedHeaders,
      benchmarkType: type,
      sortIndex,
      benchmarkFilter
    });
  };

  const onChangeTestSet = testSetId => {
    if (!testSetId) {
      setTestItems(testItemsArray.current);
      return;
    }

    const filteredTestItems = [
      ...testItemsArray.current.filter(ti => ti.testSetIds.includes(testSetId))
    ];

    if (!filteredTestItems.length) {
      resetSelectedHeaders();
    }
    const newTestCols = selectedHeaders.map(col => {
      const testItem = filteredTestItems.find(ti => ti.id === col.id);
      if (!testItem || !testItem?.testSetIds.includes(testSetId)) {
        return { id: null, sortDirection: SORT_TYPES.DOWN };
      } else {
        return col;
      }
    });
    setSelectedHeaders([...newTestCols]);
    setTestItems(filteredTestItems);

    updateRowData({
      persons: usersArray.current,
      testCols: newTestCols,
      benchmarkType: type,
      benchmarkFilter,
      sortIndex
    });
  };

  const onChangePersonCol = persons => {
    if (persons.length > 0) {
      const filteredPersons = [...usersArray.current].filter(u =>
        persons.find(a => a.value === u.id)
      );
      setPersons(filteredPersons);
    } else {
      setPersons([...usersArray.current]);
    }
  };

  const onChangeTestCol = cols => {
    updateRowData({
      persons: usersArray.current,
      testCols: cols,
      benchmarkType: type,
      benchmarkFilter,
      sortIndex
    });
  };

  const updateRowData = ({
    persons,
    testCols,
    benchmarkType,
    benchmarkFilter,
    sortIndex
  }) => {
    const rows = getDataRows({
      persons,
      testCols,
      benchmarkFilter
    });

    if (benchmarkType === BenchmarkTypes.AVERAGE) {
      const allRows = getDataRows({
        persons: usersArray.current,
        testCols,
        benchmarkFilter
      });
      testCols.forEach((col, index) => {
        if (col.id) {
          const [id, laterality] = col.id.split('_');
          col.average = getAverageOfGroup(allRows, id, index, laterality);
        }
      });
    }

    setRows(rows);

    if (sortIndex === -1 || !testCols?.[sortIndex]?.id) {
      setPersons(
        sort([...persons], {
          keys: [{ key: 'label', desc: personsSortDirection }]
        })
      );
    } else {
      const sortedRows = sortResultRowsOnCol(
        rows,
        sortIndex,
        testCols?.[sortIndex],
        benchmarkType
      );
      setPersons(sortByArray(persons, Object.keys(sortedRows)));
    }
  };

  const onClickCell = (
    colIndex,
    user,
    testItem,
    laterality,
    average = null
  ) => {
    const sessionResults = user.results?.[testItem.id];
    let data = [];

    setSelectedRow(user.id);

    const values = sessionResults?.reduce(
      (item, session) => {
        const percentage = session?.benchmarked?.[laterality]?.percentage;
        const result = session?.result?.[laterality]?.[0];

        if (result) {
          if (average) {
            item.avg = average;
          }

          if (result < item.min) item.min = Number(result);
          if (result > item.max) item.max = Number(result);

          if (session.testDate || session.sessionDate) {
            const date = session.testDate
              ? format(session.testDate, 'dd/LL/yyyy')
              : format(session.sessionDate, 'dd/LL/yyyy');
            const sortDate = session.testDate
              ? format(session.testDate, 'yyyy-LL-dd')
              : format(session.sessionDate, 'yyyy-LL-dd');

            //
            // testSessionId: ID
            // const url = `sessions/${rootEntityId}/${testSessionId}/sporter/${user.id}`;

            let yValue = result;
            switch (testItem.input_type) {
              case 'select_with_options':
                result.label = result.labels?.[locale] || result.label;
                yValue = 0; // For select with options we set y to 0
                break;
              case 'derived':
                // For derived items we need to check if result is numeric or a string
                if (isNaN(result)) {
                  yValue = 0; // If it's not numeric, we set y to 0
                }
                break;
              default:
                // For other input types, we keep the numeric value
                yValue = Number(result);
                break;
            }

            data.push({
              x: `${sortDate}`,
              y: yValue,
              result,
              date: date,
              sortDate,
              unit: session.unit,
              percentile: percentage,
              inputType: testItem.input_type
              //   url: url
            });
          }
        }
        return item;
      },
      {
        min: 0,
        max: 0,
        avg: null
      }
    );

    values.benchmarkType = type;

    data = sort(data, {
      keys: [{ key: 'sortDate', dataType: SORT_DATA_TYPES.DATE_STRING }]
    });

    /* Under normal circumstances we will never take 2 equal test on the same
     ** day and the use both of them. So we'll remove the first test
     ** for now: */
    data = data.filter((value, index, array) => {
      return (
        array.findLastIndex(value2 => {
          return value2.date === value.date;
        }) === index
      );
    });

    if (data.length === 0) return;

    const dataObj = {
      title: user.label,
      subTitle: testItem.title,
      laterality,
      chartData: {
        ...values,
        id: testItem.id,
        data: [...data]
      }
    };
    setSidePanelData(dataObj);
  };

  const onCloseSidePanel = () => setSidePanelData(null);

  const onSort = (sortIndex, sortDirection) => {
    const newTestCols = [...selectedHeaders];
    newTestCols[sortIndex].sortDirection = sortDirection;

    setSelectedHeaders([...newTestCols]);
    setSortIndex(sortIndex);
    updateRowData({
      persons: usersArray.current,
      testCols: newTestCols,
      benchmarkType: type,
      benchmarkFilter,
      sortIndex
    });
  };

  const onSortPersons = () => {
    const newPersons = sort([...persons], {
      keys: [{ key: 'label', desc: !personsSortDirection }]
    });

    if (sortIndex > -1) {
      selectedHeaders[sortIndex].sortDirection = SORT_TYPES.DOWN;
      setSortIndex(-1);
    }
    setSelectedHeaders([...selectedHeaders]);
    setPersonsSortDirection(!personsSortDirection);
    setPersons(newPersons);
  };

  return (
    <ResultsPanelContext.Provider
      value={{
        rows,
        persons,
        usersArray,
        testItems,
        groupedTestItems,
        testSets,
        onChangePersonCol,
        onChangeTestCol,
        onClickCell,
        onChangeBenchmark,
        onChangeType,
        onChangeBenchmarkFilter,
        onChangeTestSet,
        benchmarkId,
        type,
        benchmarkFilter,
        sidePanelData,
        onCloseSidePanel,
        onSort,
        onSortPersons,
        sortIndex,
        setSelectedRow,
        selectedRow,
        personsSortDirection,
        loading,
        error
      }}
    >
      {children}
    </ResultsPanelContext.Provider>
  );
};

function useResultsPanelContext() {
  const context = useContext(ResultsPanelContext);
  if (context === undefined) {
    throw new Error(
      'The ResultsPanelContext hook must be used within a ResultsPanelContext.Provider'
    );
  }
  return context;
}

export { ResultsPanelContextProvider, useResultsPanelContext };
